import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";
import { isEmpty } from "../../../../utilities/typeChecking";
import { getWatchLists, notifyWarning } from "../../../../actions";

import SearchWatchListEntriesByWatchListForm from "../../components/search-watch-list-entries-by-watch-list-form";
import {
  watchListEntriesByWatchListSelector,
  watchListsOptionsSelector,
  watchListEntriesByWatchListPageInfoSelector,
  watchListEntriestExportCsv,
  getFiltersWatchListEntriesByWatchList,
  watchListDeactivateIsLoadingSelector,
  watchListAllEntriesExportIsLoadingSelector,
  watchListEntriesByWatchListIsLoadingSelector,
  watchListEntriesExportIsLoadingSelector,
} from "../../selectors";
import WatchListEntriesTable from "../../components/watch-list-entries-table";
import {
  setWatchListEntryByWatchListSearchFilters,
  getWatchListEntriesByWatchList,
  clearWatchListEntriesByWatchListActions,
  deleteWatchListEntry,
  getWatchListEntriesByWatchListCount,
  setCurrentPageWatchListEntryByWatchList,
  setItemsPerPageWatchListEntryByWatchList,
  watchListDeactivation,
} from "../../actions";

import "./watchListEntriesByWatchListPage.css";
import Pagination from "../../../../components/Pagination/Pagination";
import { ButtonWithSpinner, PageTitle } from "../../../../components/common";
import FullscreenLoader from "../../../../layouts/FullScreenLoader";
import ConfirmationModal from "../../../../components/modals/confirmation-modal";
import useRoles from "../../../../components/hooks/useRoles";

const WatchListEntriesByWatchListPage = () => {
  const dispatch = useDispatch();
  const [isOpenCollapse, setIsOpenCollapse] = useState(true);
  const toggleCollapse = () => setIsOpenCollapse(!isOpenCollapse);
  const { watchListId } = useSelector(getFiltersWatchListEntriesByWatchList);
  const watchListEntries = useSelector(watchListEntriesByWatchListSelector);
  const { count, itemsPerPage, currentPage } = useSelector(
    watchListEntriesByWatchListPageInfoSelector
  );
  const watchLists = useSelector(watchListsOptionsSelector);
  const csvFile = useSelector(watchListEntriestExportCsv);
  const deactivateLoading = useSelector(watchListDeactivateIsLoadingSelector);
  const exportAllLoading = useSelector(
    watchListAllEntriesExportIsLoadingSelector
  );
  const exportLoading = useSelector(watchListEntriesExportIsLoadingSelector);
  const entriesLoading = useSelector(
    watchListEntriesByWatchListIsLoadingSelector
  );
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const { isAuditor, isManager, isMaster } = useRoles();
  const toggleModal = () => {
    setDeactivateModalOpen(!deactivateModalOpen);
  };

  const currentWatchList = watchLists
    ? watchLists.find((wl) => wl.value === watchListId)
    : {};

  const canDeactivate = isAuditor || isManager || isMaster;
  useEffect(() => {
    dispatch(getWatchLists());

    return () => {
      dispatch(clearWatchListEntriesByWatchListActions());
    };
  }, []);

  const handleDeactivation = (data) => {
    dispatch(watchListDeactivation(data));
  };

  useEffect(() => {
    if (!isEmpty(csvFile) && csvFile.fileBytes.size <= 3) {
      dispatch(notifyWarning("There are no records for export yet"));
    } else if (!isEmpty(csvFile) && csvFile.fileBytes.size > 3) {
      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // window.navigator.msSaveOrOpenBlob(url);
        window.navigator.msSaveOrOpenBlob(csvFile.fileBytes, csvFile.fileName);
        return;
      }

      const url = URL.createObjectURL(csvFile.fileBytes);
      const link = document.createElement("a");
      link.href = url;
      link.download = csvFile.fileName;
      link.click();
      setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(url);
      }, 100);
    }
  }, [csvFile]);

  const handleSubmit = (data) => {
    dispatch(setWatchListEntryByWatchListSearchFilters(data));
    dispatch(getWatchListEntriesByWatchListCount(data));
    dispatch(getWatchListEntriesByWatchList({ take: itemsPerPage }));
    setIsOpenCollapse(false);
  };

  const onPageChange = (_, page) => {
    dispatch(setCurrentPageWatchListEntryByWatchList(page + 1));
    const skip = page * itemsPerPage;
    dispatch(getWatchListEntriesByWatchList({ take: itemsPerPage, skip }));
  };

  const onRowsPerPageChange = ({ target }) => {
    dispatch(setCurrentPageWatchListEntryByWatchList(1));
    dispatch(setItemsPerPageWatchListEntryByWatchList(target.value));
    dispatch(getWatchListEntriesByWatchListCount({ watchListId }));
    dispatch(getWatchListEntriesByWatchList({ take: target.value }));
  };

  const handleDelete = (id) => {
    dispatch(deleteWatchListEntry(id));
    handleSubmit({ watchListId });
  };

  return (
    <div className="watch-list-entries-by-watchlist-container">
      <PageTitle title="watchlists" />
      <FullscreenLoader
        loading={
          deactivateLoading ||
          exportAllLoading ||
          entriesLoading ||
          exportLoading
        }
      />

      <SearchWatchListEntriesByWatchListForm
        handleSubmit={handleSubmit}
        watchLists={watchLists}
        isOpenCollapse={isOpenCollapse}
        toggleCollapse={toggleCollapse}
        watchListEntries={watchListEntries}
      />
      {watchListEntries.length ? null : (
        <React.Fragment>
          <div className="watch-lists-landing-box">
            {watchLists.map((item, index) => {
              return <WatchListItem key={index} item={item} />;
            })}
          </div>
          <div className="watch-list-page-background-logo" />
        </React.Fragment>
      )}
      {watchListEntries.length > 0 && (
        <React.Fragment>
          <WatchListEntriesTable
              hidePercent={true}
            canEdit={true}
            watchListEntries={watchListEntries}
            handleDelete={handleDelete}
          />
          {count && (
            <Row className="pagination-row mt-5 mb-5">
              <Col lg={2} />

              <Col lg={8} className="d-flex justify-content-center ">
                <Pagination
                  count={count}
                  rowsPerPage={itemsPerPage}
                  page={currentPage - 1}
                  onPageChange={onPageChange}
                  onRowsPerPageChange={onRowsPerPageChange}
                />
              </Col>
              {canDeactivate && (
                <Col
                  lg={2}
                  className="d-flex justify-content-end align-items-center pr-4 "
                >
                  <ConfirmationModal
                    closeModal={toggleModal}
                    isOpen={deactivateModalOpen}
                    size={"lg"}
                    modalHeader={`Deactivate ${currentWatchList.label} `}
                    confirmModal={() => handleDeactivation({ watchListId })}
                  >
                    Do you really want to deactivate this watchlist?
                  </ConfirmationModal>
                  <ButtonWithSpinner
                    text="Deactivate"
                    className="deactivate-watchlist-button "
                    outline
                    type="button"
                    forced={!watchListId}
                    onClick={toggleModal}
                    loading={deactivateLoading}
                  />
                </Col>
              )}
            </Row>
          )}
          {isOpenCollapse && watchListEntries.length ? (
            <div className="overlay-collapse-open" onClick={toggleCollapse} />
          ) : null}
        </React.Fragment>
      )}
    </div>
  );

  function WatchListItem({ item }) {
    return (
      <p
        onClick={() => handleSubmit({ watchListId: item.value })}
        className="watch-lists-landing-list-item"
      >
        {item.name}
      </p>
    );
  }
};

export default WatchListEntriesByWatchListPage;



// WEBPACK FOOTER //
// ./src/features/watch-list-entry/pages/watch-list-entries-by-watch-list-page/WatchListEntriesByWatchListPage.jsx